/**
 * TrayTasksPage - A compact tasks view for the macOS menu bar tray popover
 *
 * Shows tasks in card format grouped by:
 * - Today
 * - Tomorrow
 * - This Week
 *
 * Also includes:
 * - Current timer widget (compact) with full functionality
 * - Quick add task
 */

import type { ModuleSearchResult } from '@/lib/api/search'
import type { ReactNode } from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useAuth } from '@/components/auth/AuthProvider'
import { RecordingPermissionsDialog } from '@/components/desktop/RecordingPermissionsDialog'
import { SummaryTemplateSelect } from '@/components/meetings/SummaryTemplateSelect'
import { TaskTypeSelect } from '@/components/tasks/TaskTypeSelect'
import { LinkedModulePicker } from '@/components/time-entry/LinkedModulePicker'
import { LinkWorkSelector } from '@/components/time-entry/LinkWorkSelector'
import { SplitTimeEntryDialog } from '@/components/time-entry/SplitTimeEntryDialog'
import { useTimeEntry } from '@/components/time-entry/TimeEntryProvider'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { useCreateTaskMutation } from '@/hooks/mutations/useTaskMutations'
import { useOrganizationSettingsQuery } from '@/hooks/queries/useOrganizationSettingsQuery'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { useMyTasksData } from '@/hooks/useMyTasksData'
import { useTasksRealtime } from '@/hooks/useTasksRealtime'
import { getErrorMessage } from '@/lib/api/error-utils'
import { tasksApi } from '@/lib/api/tasks.api'
import { cn } from '@/lib/utils'
import { getLinkWorkAllowedModuleTypes } from '@/lib/utils/link-work-module-types'
import {
  getSyncedOrganizationSlug,
  hideTrayPopover,
  isTauri,
  listenForTrayPopoverShown,
  openFocusWindow,
} from '@/lib/utils/tauri'
import {
  checkRecordingPermissions,
  getRecordingSupport,
  getSystemAudioStatus,
  pauseRecording,
  resolveLiveCaptionsEnabled,
  resumeRecording,
  startRecording,
  stopRecording,
  updateRecordingLink,
  updateRecordingTemplate,
} from '@/lib/utils/tauri-recording'
import { useMyTasksStore } from '@/store/myTasks/store'
import { useMyTasksRealtime } from '@/store/myTasks/useMyTasksRealtime'
import { useOrganizationStore } from '@/store/organization.store'
import { useRecordingStore } from '@/store/recording.store'
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  ArrowRightIcon,
  ArrowsClockwiseIcon,
  CalendarBlankIcon,
  CalendarIcon,
  CaretDownIcon,
  CheckSquareIcon,
  CircleNotchIcon,
  ClockIcon,
  DotsSixVerticalIcon,
  PauseIcon,
  PlayIcon,
  PlusIcon,
  RecordIcon,
  SpinnerGapIcon,
  SquareIcon,
  StopIcon,
  TrashIcon,
  WarningCircleIcon,
  XIcon,
} from '@phosphor-icons/react'
import { toast } from 'sonner'

import type {
  PersonalTaskBucket as TaskBucket,
  TimeEntry,
} from '@scrambl/shared'
import {
  ModuleTypeEnum,
  TaskPriorityEnum,
  TaskStatusTypeEnum,
  TaskWithAssignments,
  TimeEntryModuleSettingsSchema,
} from '@scrambl/shared'

// Use the same TaskBucket type as My Tasks page for consistency
type TrayBucket = TaskBucket

interface BucketConfig {
  key: TrayBucket
  label: string
  icon: typeof WarningCircleIcon
  iconColor: string
}

const BUCKET_CONFIG: BucketConfig[] = [
  {
    key: 'today',
    label: 'Today',
    icon: CalendarIcon,
    iconColor: 'text-blue-500',
  },
  {
    key: 'tomorrow',
    label: 'Tomorrow',
    icon: CalendarBlankIcon,
    iconColor: 'text-amber-500',
  },
  {
    key: 'thisWeek',
    label: 'This Week',
    icon: CalendarBlankIcon,
    iconColor: 'text-purple-500',
  },
  {
    key: 'nextWeek',
    label: 'Next Week',
    icon: ClockIcon,
    iconColor: 'text-indigo-500',
  },
  {
    key: 'later',
    label: 'Later',
    icon: ClockIcon,
    iconColor: 'text-gray-500',
  },
  {
    key: 'unscheduled',
    label: 'Unscheduled',
    icon: CalendarBlankIcon,
    iconColor: 'text-gray-400',
  },
]

// Format elapsed time for timer
function formatElapsedTime(totalSeconds: number): string {
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  const pad = (n: number) => n.toString().padStart(2, '0')

  if (hours > 0) {
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
  }
  return `${pad(minutes)}:${pad(seconds)}`
}

function formatRecordingDuration(durationMs: number): string {
  return formatElapsedTime(Math.floor(durationMs / 1000))
}

function VuBars({ level }: { level: number }) {
  const activeBars = Math.min(5, Math.round(level * 20))

  return (
    <div className="flex h-4 items-end gap-0.5" aria-hidden>
      {Array.from({ length: 5 }, (_, index) => (
        <span
          key={index}
          className={cn(
            'w-0.5 rounded-full transition-colors',
            index < activeBars ? 'bg-red-500' : 'bg-current opacity-25',
          )}
          style={{ height: `${5 + index * 2}px` }}
        />
      ))}
    </div>
  )
}

// Priority colors for task cards
const PRIORITY_COLORS: Record<string, string> = {
  [TaskPriorityEnum.URGENT]: 'border-l-red-500',
  [TaskPriorityEnum.HIGH]: 'border-l-orange-500',
  [TaskPriorityEnum.MEDIUM]: 'border-l-blue-400',
  [TaskPriorityEnum.LOW]: 'border-l-gray-400',
}

// Sortable Task Card Component with drag handle and inline editing
function SortableTaskCard({
  task,
  onComplete,
  onNavigate,
  onUpdateName,
  isDragOverlay = false,
}: {
  task: TaskWithAssignments
  onComplete: (taskId: string) => void
  onNavigate: (task: TaskWithAssignments) => void
  onUpdateName: (taskId: string, name: string) => Promise<void>
  isDragOverlay?: boolean
}) {
  const [isCompleting, setIsCompleting] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState(task.name)
  const [isSaving, setIsSaving] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: task.id,
    disabled: isDragOverlay || isEditing,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? 'none' : transition,
    opacity: isDragging ? 0.5 : 1,
  }

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  // Update editValue when task.name changes externally
  useEffect(() => {
    if (!isEditing) {
      setEditValue(task.name)
    }
  }, [task.name, isEditing])

  const handleComplete = async (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsCompleting(true)
    try {
      await onComplete(task.id)
    } finally {
      setIsCompleting(false)
    }
  }

  const handleStartEdit = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsEditing(true)
    setEditValue(task.name)
  }

  const handleSaveEdit = async () => {
    const trimmedValue = editValue.trim()
    if (!trimmedValue || trimmedValue === task.name) {
      setIsEditing(false)
      setEditValue(task.name)
      return
    }

    setIsSaving(true)
    try {
      await onUpdateName(task.id, trimmedValue)
      setIsEditing(false)
    } catch {
      // Revert on error
      setEditValue(task.name)
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancelEdit = () => {
    setIsEditing(false)
    setEditValue(task.name)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSaveEdit()
    } else if (e.key === 'Escape') {
      handleCancelEdit()
    }
  }

  return (
    <div
      ref={setNodeRef}
      style={{ ...style, maxWidth: 'calc(100vw - 32px)' }}
      className={cn(
        'group box-border flex w-full items-center gap-1.5 overflow-hidden rounded-lg border border-l-[3px] border-border/50 bg-card/80 px-1.5 py-2 transition-all duration-150 hover:border-border hover:bg-card hover:shadow-sm',
        !isEditing && 'cursor-pointer',
        PRIORITY_COLORS[task.priority || TaskPriorityEnum.MEDIUM] ||
          'border-l-gray-400',
        isDragging && 'shadow-lg ring-2 ring-primary/20',
        isEditing && 'ring-2 ring-primary/40',
      )}
      onClick={() => !isDragging && !isEditing && onNavigate(task)}
    >
      {/* Drag handle — revealed on hover to keep rows quiet at rest */}
      <button
        className={cn(
          'flex h-5 w-5 flex-shrink-0 cursor-grab touch-none items-center justify-center rounded text-muted-foreground/50 opacity-0 transition-opacity hover:bg-muted/50 hover:text-muted-foreground focus-visible:opacity-100 active:cursor-grabbing group-hover:opacity-100',
          isEditing && 'pointer-events-none',
        )}
        {...attributes}
        {...listeners}
        onClick={e => e.stopPropagation()}
        disabled={isEditing}
      >
        <DotsSixVerticalIcon className="h-3.5 w-3.5" />
      </button>

      {/* Complete checkbox */}
      <button
        className={cn(
          'transition-tonal flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-[5px] border-[1.5px] border-muted-foreground/40 hover:border-primary hover:bg-primary/10',
          (isCompleting || isEditing) && 'opacity-50',
        )}
        onClick={handleComplete}
        disabled={isCompleting || isEditing}
      >
        {isCompleting && <SpinnerGapIcon className="h-3 w-3 animate-spin" />}
      </button>

      {/* Task info - editable */}
      <div className="min-w-0 flex-1 overflow-hidden">
        {isEditing ? (
          <input
            ref={inputRef}
            type="text"
            value={editValue}
            onChange={e => setEditValue(e.target.value)}
            onBlur={handleSaveEdit}
            onKeyDown={handleKeyDown}
            disabled={isSaving}
            className="w-full border-none bg-transparent p-0 text-sm font-medium outline-none focus:ring-0"
            onClick={e => e.stopPropagation()}
          />
        ) : (
          <p
            className="cursor-text overflow-hidden text-ellipsis whitespace-nowrap text-sm font-medium leading-tight"
            onDoubleClick={handleStartEdit}
          >
            {task.name}
          </p>
        )}
        {task.moduleType === ModuleTypeEnum.PROJECT &&
          task.moduleInfo?.name && (
            <p className="mt-0.5 overflow-hidden text-ellipsis whitespace-nowrap text-xs text-muted-foreground">
              {task.moduleInfo.name}
            </p>
          )}
      </div>

      {/* Navigate arrow - hide when editing */}
      {!isEditing && (
        <ArrowRightIcon className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground/40 opacity-0 transition-opacity group-hover:opacity-100" />
      )}
      {isSaving && (
        <SpinnerGapIcon className="h-3.5 w-3.5 flex-shrink-0 animate-spin text-muted-foreground" />
      )}
    </div>
  )
}

// Inline Add Task Input for buckets
function BucketAddTaskInput({
  bucket,
  onAdd,
}: {
  bucket: TrayBucket
  onAdd: (
    name: string,
    bucket: TrayBucket,
    taskTypeDefId: string | null,
  ) => void
}) {
  const [value, setValue] = useState('')
  const [taskTypeDefId, setTaskTypeDefId] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleSubmit = () => {
    const trimmed = value.trim()
    if (trimmed) {
      onAdd(trimmed, bucket, taskTypeDefId)
      setValue('')
      // Keep focus for continuous entry
      requestAnimationFrame(() => {
        inputRef.current?.focus()
      })
    }
  }

  return (
    <div className="flex items-center gap-2 px-2 py-1">
      <PlusIcon className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground/70" />
      <TaskTypeSelect
        value={taskTypeDefId}
        onValueChange={setTaskTypeDefId}
        triggerDisplay="icon"
      />
      <Input
        ref={inputRef}
        value={value}
        onChange={e => setValue(e.target.value)}
        placeholder="Add private task..."
        className="h-7 border-none bg-transparent text-xs shadow-none placeholder:text-muted-foreground/50 focus-visible:ring-1 focus-visible:ring-primary/20"
        onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault()
            handleSubmit()
          } else if (e.key === 'Escape') {
            setValue('')
            inputRef.current?.blur()
          }
        }}
      />
    </div>
  )
}

// Task Bucket Section with sortable tasks
function TaskBucketSection({
  config,
  tasks,
  isCollapsed,
  onToggle,
  onComplete,
  onNavigate,
  onUpdateName,
  onAddTask,
}: {
  config: BucketConfig
  tasks: TaskWithAssignments[]
  isCollapsed: boolean
  onToggle: () => void
  onComplete: (taskId: string) => void
  onNavigate: (task: TaskWithAssignments) => void
  onUpdateName: (taskId: string, name: string) => Promise<void>
  onAddTask: (
    name: string,
    bucket: TrayBucket,
    taskTypeDefId: string | null,
  ) => void
}) {
  const Icon = config.icon
  const count = tasks.length
  const taskIds = tasks.map(t => t.id)

  return (
    <div className="w-full max-w-full space-y-1.5 overflow-hidden">
      {/* Header */}
      <button
        className="group flex w-full items-center gap-2 rounded-md px-1.5 py-1 transition-colors hover:bg-accent/50"
        onClick={onToggle}
      >
        <Icon className={cn('h-3.5 w-3.5', config.iconColor)} weight="fill" />
        <span className="text-[13px] font-medium">{config.label}</span>
        <span className="text-[11px] font-medium tabular-nums text-muted-foreground">
          {count}
        </span>
        <CaretDownIcon
          className={cn(
            'ml-auto h-3 w-3 text-muted-foreground/0 transition-transform group-hover:text-muted-foreground/60',
            isCollapsed && '-rotate-90 text-muted-foreground/70',
          )}
        />
      </button>

      {/* Tasks */}
      {!isCollapsed && (
        <div className="space-y-1.5 overflow-hidden pl-1">
          <SortableContext
            items={taskIds}
            strategy={verticalListSortingStrategy}
          >
            {tasks.map(task => (
              <SortableTaskCard
                key={task.id}
                task={task}
                onComplete={onComplete}
                onNavigate={onNavigate}
                onUpdateName={onUpdateName}
              />
            ))}
          </SortableContext>
          {/* Add task input at the bottom of each bucket */}
          <BucketAddTaskInput bucket={config.key} onAdd={onAddTask} />
        </div>
      )}
    </div>
  )
}

// Full-featured Compact Timer Widget (mirrors TimeEntryWidget functionality)
function CompactTimerWidget() {
  const {
    activeEntry,
    isTracking,
    isPaused,
    isInitialized,
    startTimer,
    stopTimer,
    pauseTimer,
    resumeTimer,
    discardTimer,
    updateActiveEntry,
  } = useTimeEntry()
  const { data: settingsData } = useOrganizationSettingsQuery()
  const timeSettings = TimeEntryModuleSettingsSchema.parse(
    settingsData?.effectiveSettings?.modules?.TIME_ENTRY || {},
  )
  const linkWorkAllowedModuleTypes = getLinkWorkAllowedModuleTypes(
    timeSettings.allowedBillToTypes,
  )

  const [isDetailsOpen, setIsDetailsOpen] = useState(false)
  const [isStarting, setIsStarting] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isStopping, setIsStopping] = useState(false)
  const [showValidationWarning, setShowValidationWarning] = useState(false)
  const [localDescription, setLocalDescription] = useState(
    activeEntry?.description || '',
  )
  const descriptionRef = useRef(localDescription)
  const [pendingSplitEntry, setPendingSplitEntry] = useState<TimeEntry | null>(
    null,
  )
  const [isSplitDialogOpen, setIsSplitDialogOpen] = useState(false)
  const [isSplitRequired, setIsSplitRequired] = useState(false)
  const [splitTotalMinutes, setSplitTotalMinutes] = useState<number | null>(
    null,
  )
  const [splitEndTime, setSplitEndTime] = useState<Date | null>(null)
  const [resumeAfterSplitCancel, setResumeAfterSplitCancel] = useState(false)

  // Sync local description when activeEntry changes (e.g., on initial load)
  useEffect(() => {
    if (
      activeEntry?.description !== undefined &&
      activeEntry.description !== descriptionRef.current
    ) {
      setLocalDescription(activeEntry.description)
      descriptionRef.current = activeEntry.description
    }
  }, [activeEntry?.description])

  // Clear validation warning when user fills in description or links a module
  useEffect(() => {
    if (showValidationWarning) {
      const hasDescription =
        localDescription && localDescription.trim().length > 0
      const hasModuleLink =
        (activeEntry?.moduleType && activeEntry?.moduleId) ||
        (activeEntry?.linkedModules?.length || 0) > 0
      if (hasDescription || hasModuleLink) {
        setShowValidationWarning(false)
      }
    }
  }, [
    localDescription,
    activeEntry?.moduleType,
    activeEntry?.moduleId,
    activeEntry?.linkedModules,
    showValidationWarning,
  ])

  // Resolved linked modules
  const resolvedLinkedModules = (() => {
    if (activeEntry?.linkedModules?.length) {
      return activeEntry.linkedModules
    }
    if (activeEntry?.moduleType && activeEntry?.moduleId) {
      return [
        {
          moduleType: activeEntry.moduleType as ModuleTypeEnum,
          moduleId: activeEntry.moduleId,
          moduleName: activeEntry.moduleName ?? null,
        },
      ]
    }
    return []
  })()

  // Save description when popover closes
  const handlePopoverChange = async (open: boolean) => {
    if (!open && localDescription !== activeEntry?.description) {
      await updateActiveEntry({ description: localDescription })
    }
    setIsDetailsOpen(open)
  }

  // Save description on blur
  const handleDescriptionBlur = async () => {
    if (localDescription !== activeEntry?.description) {
      await updateActiveEntry({ description: localDescription })
    }
  }

  // Handle linked modules change
  const handleLinkedModulesChange = async (
    nextModules: typeof resolvedLinkedModules,
  ) => {
    if (nextModules.length === 1) {
      const mod = nextModules[0]
      await updateActiveEntry({
        linkedModules: nextModules,
        moduleType: mod.moduleType,
        moduleId: mod.moduleId,
        moduleName: mod.moduleName ?? null,
      })
      return
    }

    if (nextModules.length > 1) {
      await updateActiveEntry({
        linkedModules: nextModules,
        moduleType: null,
        moduleId: null,
        moduleName: null,
      })
      return
    }

    await updateActiveEntry({
      linkedModules: [],
      moduleType: null,
      moduleId: null,
      moduleName: null,
    })
  }

  const handleStart = async () => {
    setIsStarting(true)
    try {
      await startTimer()
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to start timer'))
    } finally {
      setIsStarting(false)
    }
  }

  const handleStop = async () => {
    // Check if we have at least a description or module link
    const hasDescription =
      localDescription && localDescription.trim().length > 0
    const hasModuleLink =
      (activeEntry?.moduleType && activeEntry?.moduleId) ||
      (activeEntry?.linkedModules?.length || 0) > 0

    if (!hasDescription && !hasModuleLink) {
      // Open the popup to prompt user to fill details and show warning
      setIsDetailsOpen(true)
      setShowValidationWarning(true)
      return
    }

    setIsStopping(true)
    try {
      // Save any pending description changes before stopping
      if (localDescription !== activeEntry?.description) {
        await updateActiveEntry({ description: localDescription })
      }

      // Check if we need to split across multiple linked modules
      if (
        activeEntry &&
        !activeEntry.timerSessionId &&
        resolvedLinkedModules.length > 1
      ) {
        const elapsedSeconds = activeEntry.elapsed || 0
        const roundedMinutes = Math.round(elapsedSeconds / 60)
        setSplitTotalMinutes(
          Math.max(elapsedSeconds > 0 ? 1 : 0, roundedMinutes),
        )

        let endTime = activeEntry.pausedAt
          ? new Date(activeEntry.pausedAt)
          : new Date()
        let shouldResume = false
        if (!isPaused) {
          const pausedEntry = await pauseTimer()
          if (pausedEntry) {
            shouldResume = true
            if (pausedEntry.pausedAt) {
              endTime = new Date(pausedEntry.pausedAt)
            }
          }
        }

        setSplitEndTime(endTime)
        setResumeAfterSplitCancel(shouldResume)
        setPendingSplitEntry({
          ...activeEntry,
          linkedModules: resolvedLinkedModules,
        })
        setIsSplitDialogOpen(true)
        setIsSplitRequired(true)
        setIsDetailsOpen(false)
        return
      }

      const entry = await stopTimer()
      if (entry) {
        setIsDetailsOpen(false)
        setShowValidationWarning(false)
        toast.success('Time entry saved')
      }
    } finally {
      setIsStopping(false)
    }
  }

  const handleDiscard = async () => {
    setIsDeleting(true)
    try {
      await discardTimer()
      setIsDetailsOpen(false)
      setShowValidationWarning(false)
      toast.success('Timer discarded')
    } finally {
      setIsDeleting(false)
    }
  }

  const resetSplitState = () => {
    setIsSplitDialogOpen(false)
    setPendingSplitEntry(null)
    setIsSplitRequired(false)
    setSplitTotalMinutes(null)
    setSplitEndTime(null)
    setResumeAfterSplitCancel(false)
  }

  const handleSplitCancel = async () => {
    if (resumeAfterSplitCancel) {
      await resumeTimer()
    }
    resetSplitState()
  }

  const splitDialog = (
    <SplitTimeEntryDialog
      open={isSplitDialogOpen}
      entry={pendingSplitEntry}
      requireCompletion={isSplitRequired}
      allowCancel
      totalMinutesOverride={splitTotalMinutes ?? undefined}
      beforeSplit={async () => {
        const endTime = splitEndTime ?? new Date()
        return stopTimer({ endTime })
      }}
      onCancel={handleSplitCancel}
      onOpenChange={setIsSplitDialogOpen}
      onSplitComplete={() => {
        resetSplitState()
        setShowValidationWarning(false)
        toast.success('Time entries created')
      }}
    />
  )

  if (!isInitialized) {
    return <Skeleton className="h-7 w-[72px] rounded-full" />
  }

  // Show loading state when starting timer
  if (isStarting) {
    return (
      <div className="flex h-7 items-center gap-1.5 rounded-full border border-border/60 bg-card px-2.5 text-xs text-muted-foreground">
        <CircleNotchIcon className="h-3.5 w-3.5 animate-spin" />
        <span>Starting...</span>
      </div>
    )
  }

  if (!activeEntry) {
    return (
      <>
        <Button
          variant="outline"
          size="sm"
          className="h-7 gap-1.5 rounded-full px-2.5 text-xs"
          onClick={handleStart}
          disabled={isStarting}
          title="Start timer"
        >
          <ClockIcon className="h-3.5 w-3.5" />
          Timer
        </Button>
        {splitDialog}
      </>
    )
  }

  return (
    <>
      <div
        className={cn(
          'flex h-7 items-center gap-1 rounded-full border pl-2.5 pr-1',
          isPaused
            ? 'border-amber-200 bg-amber-50/50 dark:border-amber-800 dark:bg-amber-950/20'
            : 'border-primary/20 bg-primary/5',
        )}
      >
        <div
          className={cn(
            'h-1.5 w-1.5 flex-shrink-0 rounded-full',
            isPaused ? 'bg-amber-500' : 'animate-pulse bg-green-500',
          )}
        />
        <span
          className={cn(
            'font-mono text-xs font-medium tabular-nums',
            isPaused ? 'text-amber-700 dark:text-amber-300' : '',
          )}
        >
          {formatElapsedTime(activeEntry.elapsed || 0)}
        </span>

        {/* Controls */}
        <div className="flex flex-shrink-0 items-center">
          {/* Play/Pause Button */}
          {isTracking ? (
            <Button
              variant="ghost"
              size="icon"
              className="h-5 w-5 rounded-full"
              onClick={() => pauseTimer()}
              title="Pause"
            >
              <PauseIcon className="h-3 w-3" />
            </Button>
          ) : isPaused ? (
            <Button
              variant="ghost"
              size="icon"
              className="h-5 w-5 rounded-full"
              onClick={() => resumeTimer()}
              title="Resume"
            >
              <PlayIcon className="h-3 w-3" />
            </Button>
          ) : null}

          {/* Stop Button */}
          <Button
            variant="ghost"
            size="icon"
            className="h-5 w-5 rounded-full hover:bg-green-100 hover:text-green-600 dark:hover:bg-green-900/30"
            onClick={handleStop}
            disabled={isStopping}
            title="Stop and Save"
          >
            {isStopping ? (
              <CircleNotchIcon className="h-3 w-3 animate-spin" />
            ) : (
              <SquareIcon className="h-3 w-3" />
            )}
          </Button>

          {/* Details Dropdown */}
          <Popover open={isDetailsOpen} onOpenChange={handlePopoverChange}>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-5 w-5 rounded-full"
                title="Edit Details"
              >
                <CaretDownIcon className="h-3 w-3" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-3" align="end" side="bottom">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium">Time Entry Details</h4>
                  <span className="font-mono text-sm font-medium">
                    {formatElapsedTime(activeEntry.elapsed || 0)}
                  </span>
                </div>

                {showValidationWarning && (
                  <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
                    <WarningCircleIcon className="mt-0.5 h-4 w-4 flex-shrink-0" />
                    <span>
                      Add a description or link to a project/task before
                      stopping.
                    </span>
                  </div>
                )}

                <div className="space-y-2">
                  <div>
                    <Label htmlFor="description" className="text-xs">
                      Description
                    </Label>
                    <Textarea
                      id="description"
                      placeholder="What are you working on?"
                      className={cn(
                        'mt-1 resize-none text-sm',
                        showValidationWarning &&
                          !localDescription?.trim() &&
                          'border-amber-400 focus-visible:ring-amber-400',
                      )}
                      rows={2}
                      value={localDescription}
                      onChange={e => setLocalDescription(e.target.value)}
                      onBlur={handleDescriptionBlur}
                      autoFocus
                    />
                  </div>

                  <LinkedModulePicker
                    label="Link Work (Optional)"
                    className="w-full"
                    value={resolvedLinkedModules}
                    onChange={handleLinkedModulesChange}
                    allowedModuleTypes={linkWorkAllowedModuleTypes}
                  />
                </div>

                <div className="flex items-center justify-between border-t pt-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleDiscard}
                    disabled={isDeleting}
                    className="h-7 text-xs text-destructive hover:text-destructive"
                  >
                    {isDeleting ? (
                      <CircleNotchIcon className="mr-1 h-3 w-3 animate-spin" />
                    ) : (
                      <XIcon className="mr-1 h-3 w-3" />
                    )}
                    {isDeleting ? 'Deleting...' : 'Delete'}
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleStop}
                    disabled={isStopping}
                    className="h-7 bg-green-600 text-xs hover:bg-green-700"
                  >
                    {isStopping ? (
                      <CircleNotchIcon className="mr-1 h-3 w-3 animate-spin" />
                    ) : (
                      <SquareIcon className="mr-1 h-3 w-3" />
                    )}
                    {isStopping ? 'Saving...' : 'End Timer'}
                  </Button>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>
      {splitDialog}
    </>
  )
}

function CompactRecordingWidget() {
  const state = useRecordingStore(s => s.state)
  const durationMs = useRecordingStore(s => s.durationMs)
  const levels = useRecordingStore(s => s.levels)
  const linkedModule = useRecordingStore(s => s.linkedModule)
  const setLinkedModule = useRecordingStore(s => s.setLinkedModule)
  const summaryTemplate = useRecordingStore(s => s.summaryTemplate)
  const setSummaryTemplate = useRecordingStore(s => s.setSummaryTemplate)
  const activeEventKey = useRecordingStore(s => s.activeEventKey)
  const systemAudioCaptured = useRecordingStore(s => s.systemAudioCaptured)

  const { checkFeatureFlag } = useFeatureFlags()
  const [isSupported, setIsSupported] = useState(false)
  const [isBusy, setIsBusy] = useState(false)
  const [isOptionsOpen, setIsOptionsOpen] = useState(false)
  const [showPermissionsDialog, setShowPermissionsDialog] = useState(false)
  const systemAudioModeRef = useRef<string>('none')

  useEffect(() => {
    if (!isTauri()) {
      setIsSupported(false)
      return
    }

    void getRecordingSupport().then(support => {
      setIsSupported(support.supported)
      systemAudioModeRef.current = support.systemAudioMode
    })
  }, [])

  const isRecording = state === 'recording'
  const isPaused = state === 'paused'
  const isFinalizing = state === 'finalizing'
  const isActive = isRecording || isPaused || isFinalizing

  const linkValue = useMemo(
    () =>
      linkedModule
        ? {
            moduleType: linkedModule.moduleType as ModuleTypeEnum,
            moduleId: linkedModule.moduleId,
            moduleName: linkedModule.moduleName,
          }
        : null,
    [linkedModule],
  )

  const handleModuleSelect = (result: ModuleSearchResult | null) => {
    const next = result
      ? {
          moduleType: result.type,
          moduleId: result.id,
          moduleName: result.name ?? '',
        }
      : null

    setLinkedModule(next)

    if (isActive) {
      void updateRecordingLink(
        next?.moduleType ?? null,
        next?.moduleId ?? null,
      ).catch(() => {})
    }
  }

  const handleTemplateChange = (templateRef: string | null) => {
    setSummaryTemplate(templateRef)

    if (isActive) {
      void updateRecordingTemplate(templateRef).catch(() => {})
    }
  }

  const needsSystemAudioPrompt = async () => {
    if (systemAudioModeRef.current !== 'tap') return false
    const status = await getSystemAudioStatus().catch(() => 'undetermined')
    return status === 'undetermined'
  }

  const handleStart = async () => {
    setIsBusy(true)
    try {
      const permissions = await checkRecordingPermissions()
      if (
        permissions.microphone !== 'granted' ||
        (await needsSystemAudioPrompt())
      ) {
        setShowPermissionsDialog(true)
        return
      }

      await startRecording({
        moduleType: linkedModule?.moduleType,
        moduleId: linkedModule?.moduleId,
        summaryTemplate: summaryTemplate ?? undefined,
        liveCaptions: await resolveLiveCaptionsEnabled(),
      })
      toast.success('Recording started')
    } catch (error) {
      toast.error('Could not start recording', {
        description: String(
          error instanceof Error ? error.message : error,
        ).slice(0, 200),
      })
    } finally {
      setIsBusy(false)
    }
  }

  const handlePause = () => {
    void pauseRecording()
  }

  const handleResume = () => {
    void resumeRecording()
  }

  const handleStop = async () => {
    setIsBusy(true)
    try {
      await stopRecording()
      toast.success('Recording stopped', {
        description: 'Uploading and generating AI notes...',
      })
    } catch (error) {
      toast.error('Could not stop recording', {
        description: String(
          error instanceof Error ? error.message : error,
        ).slice(0, 200),
      })
    } finally {
      setIsBusy(false)
    }
  }

  const handleDiscard = async () => {
    setIsBusy(true)
    try {
      const activeId = useRecordingStore.getState().recordingId
      if (activeId) {
        useRecordingStore.getState().markDiscarded(activeId)
      }
      await stopRecording()
      toast.success('Recording discarded')
    } catch (error) {
      toast.error('Could not discard recording', {
        description: String(
          error instanceof Error ? error.message : error,
        ).slice(0, 200),
      })
    } finally {
      setIsBusy(false)
    }
  }

  useEffect(() => {
    useRecordingStore.getState().registerControls({
      pause: handlePause,
      resume: handleResume,
      stop: handleStop,
      discard: handleDiscard,
    })
  })

  if (!checkFeatureFlag('MEETING_NOTES') || !isSupported) {
    return null
  }

  const recordingOptions = (
    <div className="space-y-2">
      {/* Not portaled: a body-portaled dropdown registers as an outside
          click on the wrapping popover and dismisses it. */}
      <LinkWorkSelector
        label="Add note to"
        value={linkValue}
        onSelect={handleModuleSelect}
        compact
        closeOnSelect
        portalToBody={false}
      />
      <SummaryTemplateSelect
        value={summaryTemplate}
        onChange={handleTemplateChange}
        disabled={isFinalizing}
      />
      {activeEventKey ? (
        <p className="text-[11px] leading-snug text-muted-foreground">
          Template changes save as the default for this meeting series.
        </p>
      ) : null}
    </div>
  )

  return (
    <>
      {isActive ? (
        <div className="flex h-7 items-center gap-1 rounded-full border border-red-500/30 bg-red-500/10 pl-2.5 pr-1">
          {isFinalizing ? (
            <CircleNotchIcon className="h-3 w-3 flex-shrink-0 animate-spin text-red-500" />
          ) : (
            <span
              className={cn(
                'h-1.5 w-1.5 flex-shrink-0 rounded-full bg-red-500',
                isRecording && 'animate-pulse',
                isPaused && 'bg-amber-500',
              )}
            />
          )}
          <span className="font-mono text-xs font-medium tabular-nums">
            {formatRecordingDuration(durationMs)}
          </span>

          <div className="flex flex-shrink-0 items-center">
            {isPaused ? (
              <Button
                size="icon"
                variant="ghost"
                className="h-5 w-5 rounded-full"
                disabled={isBusy}
                onClick={handleResume}
                title="Resume recording"
              >
                <PlayIcon className="h-3 w-3" />
              </Button>
            ) : (
              <Button
                size="icon"
                variant="ghost"
                className="h-5 w-5 rounded-full"
                disabled={isBusy || isFinalizing}
                onClick={handlePause}
                title="Pause recording"
              >
                <PauseIcon className="h-3 w-3" />
              </Button>
            )}
            <Button
              size="icon"
              variant="ghost"
              className="h-5 w-5 rounded-full text-red-600 hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/30"
              disabled={isBusy || isFinalizing}
              onClick={() => void handleStop()}
              title="Stop recording"
            >
              <StopIcon className="h-3 w-3" weight="fill" />
            </Button>

            {/* Details Dropdown */}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-5 w-5 rounded-full"
                  title="Recording details"
                >
                  <CaretDownIcon className="h-3 w-3" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-72 p-3" align="end" side="bottom">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium">
                      {isFinalizing ? 'Finalizing...' : 'Recording'}
                    </h4>
                    <div className="flex items-center gap-2">
                      {isRecording ? (
                        <span className="text-red-500">
                          <VuBars level={Math.max(levels.mic, levels.system)} />
                        </span>
                      ) : null}
                      <span className="font-mono text-sm font-medium tabular-nums">
                        {formatRecordingDuration(durationMs)}
                      </span>
                    </div>
                  </div>

                  {systemAudioCaptured === false ? (
                    <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-2.5 py-2 text-xs text-amber-700 dark:text-amber-300">
                      Mic only - system audio is not being captured.
                    </div>
                  ) : null}

                  {recordingOptions}

                  <div className="border-t pt-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs text-destructive hover:text-destructive"
                      disabled={isBusy || isFinalizing}
                      onClick={() => void handleDiscard()}
                    >
                      <TrashIcon className="mr-1 h-3 w-3" />
                      Discard recording
                    </Button>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      ) : (
        <Popover open={isOptionsOpen} onOpenChange={setIsOptionsOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="h-7 gap-1.5 rounded-full px-2.5 text-xs"
              title="Record a meeting"
            >
              <RecordIcon className="h-3.5 w-3.5 text-red-500" weight="fill" />
              Record
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-3" align="end" side="bottom">
            <div className="space-y-3">
              <div>
                <h4 className="text-sm font-medium">Record meeting</h4>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {linkedModule ? linkedModule.moduleName : 'Saved to My Notes'}
                </p>
              </div>

              {recordingOptions}

              <Button
                size="sm"
                className="h-8 w-full gap-2"
                disabled={isBusy}
                onClick={() => {
                  setIsOptionsOpen(false)
                  void handleStart()
                }}
              >
                {isBusy ? (
                  <CircleNotchIcon className="h-4 w-4 animate-spin" />
                ) : (
                  <RecordIcon className="h-4 w-4" weight="fill" />
                )}
                Start recording
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      )}

      <RecordingPermissionsDialog
        open={showPermissionsDialog}
        onOpenChange={setShowPermissionsDialog}
        onGranted={() => {
          setShowPermissionsDialog(false)
          void handleStart()
        }}
      />
    </>
  )
}

/**
 * Floating rounded card that fills the (transparent) tray window. The window
 * itself has no decorations or native shadow, so this draws the popover
 * chrome: rounded corners and border. No CSS drop shadow — it gets clipped
 * at the square window bounds and reads as a rectangle behind the card.
 */
function TrayShell({ children }: { children: ReactNode }) {
  return (
    <div className="flex h-screen w-screen flex-col bg-transparent p-2">
      <div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden rounded-2xl border border-border/60 bg-[radial-gradient(circle_at_top_left,hsl(var(--primary)/0.14),transparent_38%),hsl(var(--background))]">
        {children}
      </div>
    </div>
  )
}

export default function TrayTasksPage() {
  const { user } = useAuth()
  const { currentOrganization } = useOrganizationStore()
  const createTaskMutation = useCreateTaskMutation()

  // Track org slug for this popover window (may differ from main window's React state)
  const [orgSlug, setOrgSlug] = useState<string | null>(
    currentOrganization?.slug || null,
  )
  const [orgName, setOrgName] = useState<string | null>(
    currentOrganization?.name || null,
  )

  // Mark the document so index.css makes html/body/#root transparent — the
  // Tauri window is transparent and TrayShell draws the visible card.
  useEffect(() => {
    if (!isTauri()) return undefined
    document.documentElement.setAttribute('data-tray-popover', 'true')
    return () => {
      document.documentElement.removeAttribute('data-tray-popover')
    }
  }, [])

  // Load synced org slug on mount and listen for changes (for Tauri popover)
  useEffect(() => {
    let unlisten: (() => void) | undefined

    const setup = async () => {
      if (isTauri()) {
        // Import the listener function
        const { listenForOrgSlugChanges } = await import('@/lib/utils/tauri')

        // Load initial synced slug
        const syncedSlug = await getSyncedOrganizationSlug()
        if (syncedSlug) {
          setOrgSlug(syncedSlug)
        }

        // Listen for org changes from main window
        unlisten = await listenForOrgSlugChanges(newSlug => {
          setOrgSlug(newSlug)
        })
      }
    }

    setup()

    return () => {
      if (unlisten) {
        unlisten()
      }
    }
  }, [])

  // Also sync when currentOrganization changes (for non-Tauri or initial load)
  useEffect(() => {
    if (currentOrganization?.slug) {
      setOrgSlug(currentOrganization.slug)
      setOrgName(currentOrganization.name || null)
    }
  }, [currentOrganization?.slug, currentOrganization?.name])

  // Get task bucket assignments, preferences, and collapsed state from store (for custom/pinned tasks)
  const { preferences, getTaskBucket, loadPreferences, toggleBucketCollapse } =
    useMyTasksStore()

  // Subscribe to collapsedBuckets directly for reactivity
  const collapsedBuckets = preferences?.collapsedBuckets || []
  const isBucketCollapsed = (bucket: TrayBucket) =>
    collapsedBuckets.includes(bucket)

  // Load preferences on mount
  useEffect(() => {
    loadPreferences()
  }, [loadPreferences])

  // Enable real-time updates for tasks (separate connection for tray window)
  useTasksRealtime()

  // Enable real-time updates for My Tasks preferences (ordering, collapse state, etc.)
  useMyTasksRealtime()

  // Fetch tasks - short staleTime so reopening the tray refreshes, but a long
  // gcTime so the cache survives idle stretches: reopening shows the previous
  // list instantly and refetches in the background instead of flashing a
  // skeleton and rebuilding the view.
  const { tasks, isLoading, isFetching, refresh } = useMyTasksData({
    enabled: !!user?.id,
    staleTime: 5000, // Short stale time for tray to stay fresh
    gcTime: 60 * 60 * 1000,
    includeCompleted: false,
  })

  // Track last refresh time to debounce multiple refresh triggers
  const lastRefreshRef = useRef<number>(0)
  const REFRESH_DEBOUNCE_MS = 500 // Minimum time between refreshes

  // Debounced refresh function to prevent multiple rapid refreshes
  const debouncedRefresh = () => {
    const now = Date.now()
    if (now - lastRefreshRef.current < REFRESH_DEBOUNCE_MS) {
      return // Skip if we refreshed recently
    }
    lastRefreshRef.current = now
    refresh()
    // Also reload preferences from backend to get latest bucket assignments
    loadPreferences()
  }

  // Refetch when tray window becomes visible (Tauri popover shown)
  // For Tauri, we rely on the tray-popover-shown event which is more reliable
  // than browser visibility/focus events in a hidden/shown webview
  useEffect(() => {
    let unlistenTauri: (() => void) | undefined

    if (isTauri()) {
      // Listen for Tauri-specific popover shown event (most reliable)
      listenForTrayPopoverShown(() => {
        debouncedRefresh()
      }).then(unlisten => {
        unlistenTauri = unlisten
      })
    } else {
      // For web (non-Tauri), use standard browser events
      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          debouncedRefresh()
        }
      }

      const handleFocus = () => {
        debouncedRefresh()
      }

      document.addEventListener('visibilitychange', handleVisibilityChange)
      window.addEventListener('focus', handleFocus)

      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange)
        window.removeEventListener('focus', handleFocus)
      }
    }

    return () => {
      if (unlistenTauri) {
        unlistenTauri()
      }
    }
    // Depend on actual props, not function closure that's fresh every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh, loadPreferences])

  // Bucket tasks by due date - matches My Tasks page logic exactly
  const bucketedTasks = (() => {
    const buckets: Record<TrayBucket, TaskWithAssignments[]> = {
      today: [],
      tomorrow: [],
      thisWeek: [],
      nextWeek: [],
      later: [],
      unscheduled: [],
    }

    const now = new Date()
    now.setHours(0, 0, 0, 0)

    const tomorrow = new Date(now)
    tomorrow.setDate(tomorrow.getDate() + 1)

    const endOfWeek = new Date(now)
    endOfWeek.setDate(endOfWeek.getDate() + (7 - endOfWeek.getDay()))

    const endOfNextWeek = new Date(endOfWeek)
    endOfNextWeek.setDate(endOfNextWeek.getDate() + 7)

    tasks.forEach(task => {
      // Skip completed/canceled tasks
      if (
        task.statusTypeId === TaskStatusTypeEnum.COMPLETED ||
        task.statusTypeId === TaskStatusTypeEnum.CANCELED
      ) {
        return
      }

      // Check if task has a custom bucket assignment (pinned/moved by user)
      const customBucket = getTaskBucket(task.id)
      if (customBucket && buckets[customBucket as TrayBucket]) {
        buckets[customBucket as TrayBucket].push(task)
        return
      }

      // If no due date, put in unscheduled bucket
      if (!task.dueDate) {
        buckets.unscheduled.push(task)
        return
      }

      const dueDate = new Date(task.dueDate)
      dueDate.setHours(0, 0, 0, 0)

      if (dueDate < now) {
        buckets.today.push(task)
      } else if (dueDate.getTime() === now.getTime()) {
        buckets.today.push(task)
      } else if (dueDate.getTime() === tomorrow.getTime()) {
        buckets.tomorrow.push(task)
      } else if (dueDate <= endOfWeek) {
        buckets.thisWeek.push(task)
      } else if (dueDate <= endOfNextWeek) {
        buckets.nextWeek.push(task)
      } else {
        buckets.later.push(task)
      }
    })

    // Sort tasks within each bucket - respect user's custom order from preferences
    Object.keys(buckets).forEach(bucketKey => {
      const bucket = bucketKey as TrayBucket
      const order = preferences?.order?.[bucket] || []

      buckets[bucket].sort((a, b) => {
        // Check if tasks are in the custom order
        const aIndex = order.indexOf(a.id)
        const bIndex = order.indexOf(b.id)

        // If both have custom positions, sort by position
        if (aIndex !== -1 && bIndex !== -1) {
          return aIndex - bIndex
        }

        // Tasks with custom positions come first
        if (aIndex !== -1) return -1
        if (bIndex !== -1) return 1

        // For tasks without custom positions, sort by due date (earliest first)
        const aDue = a.dueDate
          ? new Date(a.dueDate).getTime()
          : Number.MAX_SAFE_INTEGER
        const bDue = b.dueDate
          ? new Date(b.dueDate).getTime()
          : Number.MAX_SAFE_INTEGER

        if (aDue !== bDue) {
          return aDue - bDue
        }

        // Then by creation date (oldest first)
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      })
    })

    return buckets
  })()

  // Toggle bucket collapse
  const handleToggleBucket = (bucket: TrayBucket) => {
    toggleBucketCollapse(bucket)
  }

  // Update task name
  const handleUpdateTaskName = async (taskId: string, name: string) => {
    try {
      await tasksApi.updateTask(taskId, { name })
      toast.success('Task updated')
      refresh()
    } catch {
      toast.error('Failed to update task')
      throw new Error('Failed to update task')
    }
  }

  // Complete a task
  const handleCompleteTask = async (taskId: string) => {
    const task = tasks.find(t => t.id === taskId)
    if (!task) return

    const completedStatus = task.availableStatuses?.find(
      status => status.statusTypeId === TaskStatusTypeEnum.COMPLETED,
    )

    if (completedStatus) {
      try {
        await tasksApi.updateTaskStatus(
          taskId,
          completedStatus.id,
          TaskStatusTypeEnum.COMPLETED,
        )
        toast.success('Task completed')
        refresh()
      } catch {
        toast.error('Failed to complete task')
      }
    }
  }

  // Navigate to task - opens in a focus window (popup)
  const handleNavigateToTask = async (task: TaskWithAssignments) => {
    if (!orgSlug) return

    // Build the task detail URL - use taskNumber if available, otherwise task id
    const taskIdentifier = task.taskNumber || task.id
    const taskUrl = `/${orgSlug}/tasks/${taskIdentifier}`

    // Hide the tray popover first
    await hideTrayPopover()

    // Open in a focus window (works like the popup windows from the main app)
    await openFocusWindow(taskUrl, task.taskNumber || task.name || 'Task')
  }

  // Add task to a specific bucket
  const handleAddTask = async (
    name: string,
    bucket: TrayBucket,
    taskTypeDefId: string | null,
  ) => {
    if (!user?.id) return

    // Calculate due date based on bucket - matches My Tasks page logic
    const getDueDateForBucket = (b: TrayBucket): Date | null => {
      const now = new Date()
      now.setHours(23, 59, 59, 999)

      switch (b) {
        case 'today':
          return now
        case 'tomorrow': {
          const tomorrow = new Date(now)
          tomorrow.setDate(tomorrow.getDate() + 1)
          return tomorrow
        }
        case 'thisWeek': {
          // End of the current week (Sunday)
          const endOfWeek = new Date(now)
          const daysUntilSunday = 7 - endOfWeek.getDay()
          endOfWeek.setDate(endOfWeek.getDate() + daysUntilSunday)
          return endOfWeek
        }
        case 'nextWeek': {
          // End of next week
          const endOfWeek = new Date(now)
          const daysUntilSunday = 7 - endOfWeek.getDay()
          endOfWeek.setDate(endOfWeek.getDate() + daysUntilSunday + 7)
          return endOfWeek
        }
        case 'later': {
          // 30 days from now
          const later = new Date(now)
          later.setDate(later.getDate() + 30)
          return later
        }
        case 'unscheduled':
          return null // No due date
        default:
          return now
      }
    }

    try {
      await createTaskMutation.mutateAsync({
        taskData: {
          name,
          priority: TaskPriorityEnum.MEDIUM,
          taskTypeDefId: taskTypeDefId || undefined,
          dueDate: getDueDateForBucket(bucket),
          moduleType: ModuleTypeEnum.MEMBER,
          moduleId: user.id,
          assignees: [user.id],
          isPrivate: true,
        },
      })
      toast.success('Task created')
      refresh()
    } catch {
      toast.error('Failed to create task')
    }
  }

  // Navigate to full My Tasks page - opens in a focus window (popup)
  const handleViewAll = async () => {
    if (!orgSlug) {
      toast.error('No organization selected')
      return
    }

    const tasksUrl = `/${orgSlug}/tasks/mine`

    // Hide the tray popover first
    await hideTrayPopover()

    // Open in a focus window
    await openFocusWindow(tasksUrl, 'My Tasks')
  }

  const handleOpenMyTime = async () => {
    if (!orgSlug) {
      toast.error('No organization selected')
      return
    }

    await hideTrayPopover()
    await openFocusWindow(`/${orgSlug}/my-time/log`, 'My Time')
  }

  // Total task count
  const totalTasks = Object.values(bucketedTasks).reduce(
    (sum, bucket) => sum + bucket.length,
    0,
  )

  // DnD sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5, // 5px movement required before drag starts
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  // Track active dragging task for overlay
  const [activeTask, setActiveTask] = useState<TaskWithAssignments | null>(null)

  // Handle drag start - track which task is being dragged
  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event
    const taskId = active.id as string

    // Find the task being dragged
    for (const bucket of Object.values(bucketedTasks)) {
      const task = bucket.find(t => t.id === taskId)
      if (task) {
        setActiveTask(task)
        break
      }
    }
  }

  // Handle drag end - reorder tasks within/across buckets
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    setActiveTask(null)

    if (!active || !over || active.id === over.id) {
      return
    }

    const activeId = active.id as string
    const overId = over.id as string

    // Find which buckets the tasks belong to
    let activeBucket: TrayBucket | null = null
    let overBucket: TrayBucket | null = null

    for (const [bucket, bucketTasks] of Object.entries(bucketedTasks)) {
      if (bucketTasks.some(t => t.id === activeId)) {
        activeBucket = bucket as TrayBucket
      }
      if (bucketTasks.some(t => t.id === overId)) {
        overBucket = bucket as TrayBucket
      }
    }

    if (!activeBucket || !overBucket) return

    const { preferences, updatePreferences, moveTaskToBucket } =
      useMyTasksStore.getState()

    if (activeBucket !== overBucket) {
      // Moving to a different bucket
      moveTaskToBucket(activeId, overBucket)
    } else {
      // Reordering within the same bucket
      const bucketTasks = bucketedTasks[activeBucket]
      const currentOrder = preferences?.order?.[activeBucket] || []

      // Build complete order including all tasks
      const completeOrder: string[] = []
      for (const taskId of currentOrder) {
        if (bucketTasks.some(t => t.id === taskId)) {
          completeOrder.push(taskId)
        }
      }
      for (const task of bucketTasks) {
        if (!completeOrder.includes(task.id)) {
          completeOrder.push(task.id)
        }
      }

      // Find indices and reorder
      const activeIndex = completeOrder.indexOf(activeId)
      const overIndex = completeOrder.indexOf(overId)

      if (activeIndex !== -1 && overIndex !== -1 && activeIndex !== overIndex) {
        const newOrder = arrayMove(completeOrder, activeIndex, overIndex)
        updatePreferences({
          order: {
            ...preferences?.order,
            [activeBucket]: newOrder,
          },
        })
      }
    }
  }

  // Full skeleton only on a cold start (no cached tasks yet). Background
  // refetches keep the previous list on screen.
  if (isLoading) {
    return (
      <TrayShell>
        <div className="flex items-center justify-between gap-2 border-b border-border/60 px-3.5 py-2.5">
          <Skeleton className="h-5 w-28" />
          <div className="flex items-center gap-1.5">
            <Skeleton className="h-7 w-[72px] rounded-full" />
            <Skeleton className="h-7 w-[84px] rounded-full" />
          </div>
        </div>
        <div className="space-y-2 p-3.5">
          <Skeleton className="h-5 w-24" />
          <Skeleton className="h-11 w-full rounded-lg" />
          <Skeleton className="h-11 w-full rounded-lg" />
          <Skeleton className="mt-4 h-5 w-24" />
          <Skeleton className="h-11 w-full rounded-lg" />
        </div>
      </TrayShell>
    )
  }

  return (
    <TrayShell>
      <div className="flex-shrink-0 border-b border-border/60 bg-background/70 px-3.5 py-2.5 backdrop-blur">
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <CheckSquareIcon
              className="h-4 w-4 flex-shrink-0 text-primary"
              weight="fill"
            />
            <h1 className="truncate text-sm font-semibold">My Tasks</h1>
            <span className="rounded-full bg-muted px-1.5 py-0.5 text-[11px] font-medium tabular-nums text-muted-foreground">
              {totalTasks}
            </span>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 rounded-full text-muted-foreground/70 hover:text-foreground"
              onClick={() => refresh()}
              title="Refresh tasks"
            >
              <ArrowsClockwiseIcon
                className={cn('h-3.5 w-3.5', isFetching && 'animate-spin')}
              />
            </Button>
          </div>
          <div className="flex flex-shrink-0 items-center gap-1.5">
            <CompactTimerWidget />
            <CompactRecordingWidget />
          </div>
        </div>
      </div>

      {/* Task Buckets with drag and drop */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <ScrollArea className="w-full flex-1 [&>div]:!overflow-x-hidden">
          <div
            className="w-full space-y-3 overflow-x-hidden px-3 py-3"
            style={{ maxWidth: '100%' }}
          >
            {BUCKET_CONFIG.filter(
              config => bucketedTasks[config.key].length > 0,
            ).map(config => (
              <TaskBucketSection
                key={config.key}
                config={config}
                tasks={bucketedTasks[config.key]}
                isCollapsed={isBucketCollapsed(config.key)}
                onToggle={() => handleToggleBucket(config.key)}
                onComplete={handleCompleteTask}
                onNavigate={handleNavigateToTask}
                onUpdateName={handleUpdateTaskName}
                onAddTask={handleAddTask}
              />
            ))}
          </div>
        </ScrollArea>

        {/* Drag overlay for smooth dragging */}
        <DragOverlay>
          {activeTask ? (
            <div
              style={{ maxWidth: 'calc(100vw - 24px)' }}
              className={cn(
                'flex cursor-grabbing items-center gap-1.5 overflow-hidden rounded-lg border border-l-[3px] border-primary/40 bg-card px-1.5 py-2 shadow-lg',
                PRIORITY_COLORS[
                  activeTask.priority || TaskPriorityEnum.MEDIUM
                ] || 'border-l-gray-400',
              )}
            >
              <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded text-muted-foreground">
                <DotsSixVerticalIcon className="h-3.5 w-3.5" />
              </div>
              <div className="h-4 w-4 flex-shrink-0 rounded-[5px] border-[1.5px] border-muted-foreground/40" />
              <div className="min-w-0 flex-1 overflow-hidden">
                <p className="overflow-hidden text-ellipsis whitespace-nowrap text-sm font-medium leading-tight">
                  {activeTask.name}
                </p>
                {activeTask.moduleType === ModuleTypeEnum.PROJECT &&
                  activeTask.moduleInfo?.name && (
                    <p className="mt-0.5 overflow-hidden text-ellipsis whitespace-nowrap text-xs text-muted-foreground">
                      {activeTask.moduleInfo.name}
                    </p>
                  )}
              </div>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Footer */}
      <div className="flex flex-shrink-0 items-center justify-between gap-2 border-t border-border/60 bg-background/70 px-3.5 py-2 backdrop-blur">
        <span className="truncate text-[11px] text-muted-foreground">
          {orgName || orgSlug || 'No organization'}
        </span>
        <div className="flex flex-shrink-0 items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 rounded-full px-2.5 text-xs text-muted-foreground hover:text-foreground"
            onClick={() => void handleOpenMyTime()}
            disabled={!orgSlug}
          >
            My Time
          </Button>
          <Button
            variant="secondary"
            size="sm"
            className="h-7 gap-1 rounded-full px-2.5 text-xs"
            onClick={handleViewAll}
            disabled={!orgSlug}
          >
            All tasks
            <ArrowRightIcon className="h-3 w-3" />
          </Button>
        </div>
      </div>
    </TrayShell>
  )
}
